/**
 * 「담은 원단」 트레이 (계획서 U1 · 루브릭 R2-1) — 이 브라우저에만, 로그인 없이 쓴다.
 *
 * localStorage('aitex.tray') 에 최대 20개. 저장소가 막혀 있어도(사생활 보호 모드) 이번 탭에서는 동작한다.
 * 다른 탭에서 바꾸면 'storage' 이벤트로 따라온다. 「컬렉션으로 저장」 때 서버로 간다.
 * 썸네일 주소는 서버 서명 URL(15분)이라 시간이 지나면 깨질 수 있다 — 카드가 새 주소로 그려질 때 갱신하고,
 * 깨지면 화면이 견본 아이콘으로 대신한다(지어낸 그림을 두지 않는다).
 */
import { useSyncExternalStore } from 'react';

export interface TrayItem {
  fabric_id: string;
  fabric_name: string;
  part_number?: string | null;
  thumb_url?: string | null;
  /** 표시용 견본(R5, 2026-10-18) — 트레이 · 비교 · 컬렉션 저장 창은 이것을 먼저, 없거나 깨지면 thumb_url */
  swatch_url?: string | null;
  /** 작은 견본(240) — 트레이 40px 칸은 이것 먼저(1배 화면), 없거나 깨지면 swatch_url */
  swatch_sm_url?: string | null;
  /** 주 색상(서버 어휘) — 이름 · 품번까지 같은 원단을 화면 낭독기에서도 가르려고 (리뷰 8회차 관찰 ①) */
  color?: string | null;
  /** 담은 시각(ms) — 최근 담은 것이 위 */
  at: number;
}

export const TRAY_KEY = 'aitex.tray';
export const TRAY_MAX = 20;
/** 비교 표는 최대 4열 (서버 /compare 도 4개까지) */
export const MAX_COMPARE_IDS = 4;
const EVT = 'aitex:tray';

function read(): TrayItem[] {
  try {
    const raw = localStorage.getItem(TRAY_KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v)
      ? v.filter((x) => x && typeof x.fabric_id === 'string').slice(0, TRAY_MAX)
      : [];
  } catch { return []; }
}

let items: TrayItem[] = read();

function write(next: TrayItem[]): void {
  items = next;
  try { localStorage.setItem(TRAY_KEY, JSON.stringify(next)); } catch { /* 저장만 못 한다 */ }
  window.dispatchEvent(new Event(EVT));
}

function subscribe(fn: () => void): () => void {
  const onStorage = (e: StorageEvent) => { if (e.key === TRAY_KEY || e.key === null) { items = read(); fn(); } };
  window.addEventListener(EVT, fn);
  window.addEventListener('storage', onStorage);
  return () => { window.removeEventListener(EVT, fn); window.removeEventListener('storage', onStorage); };
}

export function getTray(): TrayItem[] {
  return items;
}

export function useTray(): TrayItem[] {
  return useSyncExternalStore(subscribe, getTray, getTray);
}

export function useInTray(id: string): boolean {
  return useSyncExternalStore(subscribe, () => items.some((x) => x.fabric_id === id), () => false);
}

export type TrayResult = 'added' | 'removed' | 'full';

/** 담기 — 이미 있으면 그대로(썸네일만 새 주소로), 20개가 차 있으면 'full' */
export function addToTray(it: Omit<TrayItem, 'at'>): TrayResult {
  const i = items.findIndex((x) => x.fabric_id === it.fabric_id);
  if (i >= 0) return 'added';
  if (items.length >= TRAY_MAX) return 'full';
  write([{ ...it, at: Date.now() }, ...items]);
  return 'added';
}

export function removeFromTray(id: string): void {
  write(items.filter((x) => x.fabric_id !== id));
}

export function toggleTray(it: Omit<TrayItem, 'at'>): TrayResult {
  if (items.some((x) => x.fabric_id === it.fabric_id)) { removeFromTray(it.fabric_id); return 'removed'; }
  return addToTray(it);
}

export function clearTray(): void {
  write([]);
}

/**
 * 화면이 새 서명 URL 로 카드를 그리면 트레이의 썸네일 · 견본도 바꿔 둔다(15분 뒤 깨지지 않게).
 * color 를 주면(undefined 가 아니면) 주 색상도 — 담은 뒤 재분석 · 색 근거 규칙으로 바뀐 색이 트레이에 남지 않게 (2026-10-18)
 */
export function refreshTrayThumb(id: string, thumb: string | null | undefined, color?: string | null, swatch?: string | null, swatchSm?: string | null): void {
  const i = items.findIndex((x) => x.fabric_id === id);
  if (i < 0) return;
  const patch: Partial<TrayItem> = {};
  if (thumb && items[i].thumb_url !== thumb) patch.thumb_url = thumb;
  if (swatch && items[i].swatch_url !== swatch) patch.swatch_url = swatch;
  if (swatchSm && items[i].swatch_sm_url !== swatchSm) patch.swatch_sm_url = swatchSm;
  if (color !== undefined && (items[i].color ?? null) !== color) patch.color = color;
  if (!Object.keys(patch).length) return;
  const next = items.slice();
  next[i] = { ...next[i], ...patch };
  write(next);
}

/** 비교 주소 — /compare?ids=A,B[,C,D] */
export function compareHref(ids: string[]): string {
  return `/compare?ids=${ids.map(encodeURIComponent).join(',')}`;
}
